import { useEffect, useState } from "react";
import {
  Users, UserX, FlaskConical, DollarSign, Server, Wifi, UserCog, CreditCard, TrendingUp,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import AnnouncementsBanner from "@/components/AnnouncementsBanner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const COLORS = ["hsl(var(--primary))", "hsl(var(--destructive))", "hsl(var(--warning))"];

function StatCard({ icon: Icon, label, value, tone = "primary", delay = 0, testid }) {
  const tones = {
    primary: "text-primary bg-primary/10",
    success: "text-success bg-success/10",
    destructive: "text-destructive bg-destructive/10",
    warning: "text-warning bg-warning/10",
    accent: "text-accent bg-accent/10",
  };
  return (
    <Card
      className="p-5 border-border bg-card transition-transform duration-200 hover:-translate-y-0.5"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className={`grid place-items-center h-10 w-10 rounded-md ${tones[tone]}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
      </div>
      <p className="mt-4 text-3xl font-display font-bold tracking-tight tabular-nums">{value}</p>
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{label}</p>
    </Card>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card className="p-5 border-border bg-card">
      <h3 className="font-display font-semibold text-lg mb-4">{title}</h3>
      <div className="h-64">{children}</div>
    </Card>
  );
}

const tooltipStyle = {
  backgroundColor: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: "12px",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <>
        <PageHeader overline="Overview" title="Dashboard" description="Loading your control center…" />
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
        </div>
      </>
    );
  }

  const isAdmin = user.role === "super_admin";
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const creditsFmt = (n) => Number(n || 0).toLocaleString();

  return (
    <div data-testid="dashboard-page">
      <AnnouncementsBanner />
      <PageHeader
        overline="Overview"
        title={`Welcome, ${user.name?.split(" ")[0] || "there"}`}
        description="Real-time snapshot of your IPTV operation."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Active Users" value={stats.active_users} tone="success" delay={0} testid="stat-active" />
        <StatCard icon={UserX} label="Expired Users" value={stats.expired_users} tone="destructive" delay={50} testid="stat-expired" />
        <StatCard icon={FlaskConical} label="Trial Accounts" value={stats.trial_users} tone="warning" delay={100} testid="stat-trial" />
        <StatCard icon={CreditCard} label="Credits Used" value={creditsFmt(stats.total_revenue)} tone="primary" delay={150} testid="stat-revenue" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {isAdmin ? (
          <>
            <StatCard icon={Server} label="Total Panels" value={stats.total_panels} tone="accent" testid="stat-panels" />
            <StatCard icon={Wifi} label="Online Panels" value={stats.online_panels} tone="success" testid="stat-online-panels" />
            <StatCard icon={UserCog} label="Resellers" value={stats.total_resellers} tone="primary" testid="stat-resellers" />
            <StatCard icon={TrendingUp} label="Total Sales" value={stats.total_sales} tone="accent" testid="stat-sales" />
          </>
        ) : (
          <>
            <StatCard icon={CreditCard} label="Available Credits" value={creditsFmt(stats.credits)} tone="accent" testid="stat-credits" />
            <StatCard icon={Users} label="Total Customers" value={stats.total_customers} tone="primary" testid="stat-customers" />
            <StatCard icon={TrendingUp} label="Total Sales" value={stats.total_sales} tone="accent" testid="stat-sales" />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2">
          <ChartCard title="Credits Used (last 6 months)">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.revenue_chart}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${creditsFmt(v)} credits`} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <ChartCard title="Subscription Status">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={stats.status_breakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                {stats.status_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 -mt-6">
            {stats.status_breakdown.map((s, i) => (
              <div key={s.name} className="flex items-center gap-1.5 text-xs">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i] }} />
                {s.name}
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="mt-4">
        <ChartCard title="New Signups (last 7 days)">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.signups_chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="count" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
